import { createContext, useContext, useMemo, type ReactNode } from "react";
import { useAuthUser } from "../auth/session";
import type { IdentityClient } from "./IdentityClient";
import { HttpIdentityClient } from "./HttpIdentityClient";
import { MockIdentityClient, MOCK_ORG, MOCK_SELF } from "./MockIdentityClient";
import type { OrgRole } from "./types";
import { AUTH_ENABLED, IDENTITY_BASE_URL } from "../auth/config";

const IdentityClientContext = createContext<IdentityClient | null>(null);

/** Real identity client only when the auth gate is on AND the service is
 *  configured — its endpoints are cookie-authenticated, so a base URL without
 *  a session would just produce 401s. Everything else gets the mock. */
export function createDefaultIdentityClient(): IdentityClient {
  return AUTH_ENABLED && IDENTITY_BASE_URL
    ? new HttpIdentityClient(IDENTITY_BASE_URL)
    : new MockIdentityClient();
}

/* ---- Current user (who am I / what can I do) ----
 * useAuthUser only works under AuthSessionProvider, which mounts only when
 * AUTH_ENABLED — so the bridge component below is rendered only in that case,
 * and mock mode gets a static admin matching MockIdentityClient's seed
 * (self-row logic works offline). */

export interface CurrentUser {
  id: string;
  name: string;
  email: string;
  role: OrgRole;
  organizationId?: string;
  organizationName?: string;
  pictureUrl?: string;
  /** Stigg customer id for client-side SDK hardening (OPSS-55). Optional until
   *  identity surfaces it on /identity/user. */
  stiggCustomerId?: string;
}

const DEV_USER: CurrentUser = {
  ...MOCK_SELF,
  role: "ADMIN",
  organizationId: MOCK_ORG.id,
  organizationName: MOCK_ORG.name,
};

const CurrentUserContext = createContext<CurrentUser>(DEV_USER);

function AuthedCurrentUser({ children }: { children: ReactNode }) {
  const user = useAuthUser();
  const value = useMemo<CurrentUser>(
    () => ({
      id: user?.userId ?? "",
      name: user?.userName ?? "",
      email: user?.userEmail ?? "",
      role: user?.userRole ?? "MEMBER",
      organizationId: user?.organizationId,
      organizationName: user?.organizationName,
      pictureUrl: user?.userPictureUrl,
      stiggCustomerId: user?.stiggCustomerId,
    }),
    [user],
  );
  return (
    <CurrentUserContext.Provider value={value}>
      {children}
    </CurrentUserContext.Provider>
  );
}

export function IdentityProvider({
  children,
  client,
}: {
  children: ReactNode;
  client?: IdentityClient;
}) {
  const value = useMemo(
    () => client ?? createDefaultIdentityClient(),
    [client],
  );
  const body = (
    <IdentityClientContext.Provider value={value}>
      {children}
    </IdentityClientContext.Provider>
  );

  if (!AUTH_ENABLED) return body;

  return <AuthedCurrentUser>{body}</AuthedCurrentUser>;
}

export function useIdentityClient(): IdentityClient {
  const client = useContext(IdentityClientContext);
  if (!client) {
    throw new Error(
      "useIdentityClient must be used within an IdentityProvider",
    );
  }

  return client;
}

export function useCurrentUser(): CurrentUser {
  return useContext(CurrentUserContext);
}

/** The viewer's org role; gates the members page's admin-only controls. */
export function useOrgRole(): OrgRole {
  return useCurrentUser().role;
}
